import React, { useState } from "react";
import { styled } from "@mui/system";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";

import Select from "widgets/Select/Select";
import MenuItem from "widgets/MenuItem/CustomMenuItem";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

const StyledContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const Placeholder = styled("span")({
  color: "#9ca3af",
  fontSize: "0.9rem",
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const MAX_VISIBLE_ITEMS = 1; 

const MultipleSelect = ({
  id,
  name,
  value = [],
  menuItems = [],
  tag,
  placeholder = "Select",
  handleChange,
  errorMessage,
  disabled = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);

  const hasValue = value.length > 0;

  const renderValue = (selected) => {
    if (!selected || selected.length === 0) {
      return <Placeholder>{placeholder}</Placeholder>;
    }

    const selectedItems = menuItems.filter((item) =>
      selected.includes(item.value)
    );

    const visibleItems = selectedItems.slice(0, MAX_VISIBLE_ITEMS);
    const remainingCount = selectedItems.length - visibleItems.length;

    return (
      <span>
        {visibleItems.map((item) => item.label).join(", ")}
        {remainingCount > 0 && ` +${remainingCount} more`}
      </span>
    );
  };

  const handleItemClick = (itemValue) => {
    const newValue = value.includes(itemValue)
      ? value.filter((v) => v !== itemValue)
      : [...value, itemValue];

    handleChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  return (
    <StyledContainer>
      <FormControl
        fullWidth
        variant="outlined"
        disabled={disabled}
        error={Boolean(errorMessage)}
      >
        <InputLabel
          id={`${id}-label`}
          required={required}
          shrink={hasValue || open}
        >
          {tag}
        </InputLabel>

        <Select
          id={id}
          name={name}
          labelId={`${id}-label`}
          multiple
          value={value}
          displayEmpty
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderValue={renderValue}
          input={<OutlinedInput label={tag} />}
          MenuProps={MenuProps}
          sx={{
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "flex-start",
              paddingTop: "12px",
              paddingBottom: "12px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              minHeight: "44px",
            },
          }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.id ?? item.value}
              value={item.value}
              disabled={item.disabled}
              onClick={() => handleItemClick(item.value)}
            >
              <Checkbox checked={value.includes(item.value)} />
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </StyledContainer>
  );
};

export default React.memo(MultipleSelect);
